// Loudness normalization and volume for the footer player.
// CDN demos carry their measured loudness (lufs, truePeak). They play through a Web Audio gain node,
// which can also boost and also works on iOS, where HTMLMediaElement.volume is read-only.
// Everything else (SoundCloud proxy, demos without measurement) uses media.volume.

const TARGET_LUFS = -18
const MAX_TRUE_PEAK = -1
const MAX_BOOST_DB = 12
const MAX_CUT_DB = -24

let context = null
let gainNode = null
let source = null
let routedMedia = null
let unlocked = false
let current = { media: null, routed: false, gain: 1 }

const dbToGain = (db) => Math.pow(10, db / 20)

// Linear gain that brings a demo to TARGET_LUFS; boosts are limited so the true peak stays below MAX_TRUE_PEAK
export function normalizationGain(lufs, truePeak) {
  if (!Number.isFinite(lufs) || lufs <= -60) {
    return 1
  }
  let db = TARGET_LUFS - lufs
  if (db > 0 && Number.isFinite(truePeak)) {
    db = Math.min(db, Math.max(0, MAX_TRUE_PEAK - truePeak))
  }
  return dbToGain(Math.min(MAX_BOOST_DB, Math.max(MAX_CUT_DB, db)))
}

// An AudioContext only starts inside a user gesture. Until then demos play without normalization.
export function installAudioUnlock() {
  if (unlocked || typeof window === 'undefined') {
    return
  }
  const events = ['pointerdown', 'keydown', 'touchend']
  const unlock = () => {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) {
      return
    }
    try {
      context = context || new Ctx()
      context.resume()
      unlocked = true
      events.forEach((e) => window.removeEventListener(e, unlock, true))
    } catch (e) {
      console.warn('[AudioGain] AudioContext unavailable:', e?.message)
    }
  }
  events.forEach((e) => window.addEventListener(e, unlock, true))
}

// Must run before the media element loads its source: without CORS the Web Audio output is silent
export function prepareMedia(media, demo) {
  const routable = unlocked && !!demo && Number.isFinite(demo.lufs)
  if (routable) {
    media.crossOrigin = 'anonymous'
  }
  return routable
}

// Route a prepared media element through the gain node; returns false if it plays directly
export function attachMedia(media, demo, routable, volume) {
  let routed = false
  if (routable && context) {
    try {
      if (!gainNode) {
        gainNode = context.createGain()
        gainNode.connect(context.destination)
      }
      if (routedMedia !== media) {
        source?.disconnect()
        source = context.createMediaElementSource(media)
        source.connect(gainNode)
        routedMedia = media
      }
      routed = true
    } catch (e) {
      console.warn('[AudioGain] Routing failed, playing without normalization:', e?.message)
    }
  }
  current = { media, routed, gain: routed ? normalizationGain(demo.lufs, demo.truePeak) : 1 }
  applyVolume(volume)
  return routed
}

// Browsers (notably iOS) can suspend a running context, e.g. after an interruption
export function resumeAudio() {
  if (context && context.state !== 'running') {
    context.resume().catch(() => null)
  }
}

export function applyVolume(volume) {
  const v = Math.min(1, Math.max(0, Number(volume)))
  const { media, routed, gain } = current
  if (!media) {
    return
  }
  if (routed && gainNode) {
    media.volume = 1
    gainNode.gain.value = gain * v
  } else {
    media.volume = v
  }
}
